import {
  CheckCircle,
  Circle,
  LockReset,
  Visibility,
  VisibilityOff,
  VpnKey,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Fade,
  Grid,
  Grow,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { ErrorScreen, Loading } from '../../../components';
import { useToastStore } from '../../../stores';
import { colorPrimary, textFieldStyle } from '../../../theme/MainTheme';
import { AtualizarSenhaDto } from '../dto/ShopkeeperDTO';
import { atualizarSenha } from '../repository/ShopkeeperRepository';

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

interface PasswordRequirements {
  minLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
}

export function AlterarSenhaTab() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { setToast } = useToastStore();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  // Calcula os requisitos da senha
  const passwordRequirements = useMemo<PasswordRequirements>(() => {
    return {
      minLength: newPassword.length >= 8,
      hasUpperCase: /[A-Z]/.test(newPassword),
      hasLowerCase: /[a-z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
    };
  }, [newPassword]);

  // Calcula a força da senha
  const passwordStrength = useMemo<PasswordStrength>(() => {
    if (!newPassword) {
      return { score: 0, label: '', color: '#e2e8f0' };
    }

    const requirements = Object.values(passwordRequirements).filter(Boolean).length;
    
    if (requirements <= 1) {
      return { score: 1, label: 'Fraca', color: '#ff3b3b' };
    } else if (requirements === 2) {
      return { score: 2, label: 'Média', color: '#ffcc00' };
    } else if (requirements === 3) {
      return { score: 3, label: 'Boa', color: '#ff944d' };
    } else {
      return { score: 4, label: 'Forte', color: '#06c270' };
    }
  }, [newPassword, passwordRequirements]);

  const handleSubmit = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      setToast({variant:  'error', message: 'Por favor, preencha todos os campos'});
      return;
    }

    if (newPassword !== confirmPassword) {
      setToast({variant:  'error', message: 'As senhas não coincidem'});
      return;
    }

    if (passwordStrength.score < 3) {
      alert();
      setToast({variant:  'error', message: 'A senha precisa ser mais forte'});
      return;
    }
    const payload: AtualizarSenhaDto = {
      senhaAntiga: oldPassword,
      senhaNova: newPassword,
    };
      try {
        setIsLoading(true);
        const result = await atualizarSenha(payload);
        setToast({variant: result.success? 'success' : 'error', message: result.message});
      } catch (error) {
        setToast({variant:"error", message: "Erro. Contate o administrador."});
      } finally {
        setIsLoading(false);
      }
  };


  if(isLoading) return <Loading  fullScreen={true}/>
  if(error) return <ErrorScreen fullScreen={true}/>


  return (
    <Fade in timeout={600}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 720,
          width: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          bgcolor: 'white',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 4, pt: 4, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LockReset sx={{ color: colorPrimary, fontSize: 32 }} />
            <Typography variant="h5" fontWeight={700} color="#111">
              Alterar Senha
            </Typography>
          </Box>
          <Typography variant="body2" color="#6B7588" sx={{ mb: 3 }}>
            Atualize sua senha para manter sua conta segura.
          </Typography>
          <Divider />
        </Box>

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ px: 4, pb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            {/* Senha Antiga */}
            <TextField
              fullWidth
              type={showOldPassword ? 'text' : 'password'}
              label="Senha Antiga"
              placeholder="Digite sua senha atual"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              sx={textFieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                      sx={{ color: '#6B7588' }}
                    >
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Nova Senha */}
            <Box>
              <TextField
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                label="Nova Senha"
                placeholder="Digite sua nova senha"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={textFieldStyle}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        sx={{ color: '#6B7588' }}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Indicador de Força */}
              {newPassword && (
                <Grow in timeout={400}>
                  <Box sx={{ mt: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ letterSpacing: 1, color: '#9ca3af', textTransform: 'uppercase' }}
                      >
                        Força da Senha
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ letterSpacing: 1, color: passwordStrength.color, textTransform: 'uppercase' }}
                      >
                        {passwordStrength.label}
                      </Typography>
                    </Box>

                    {/* Barra de Progresso */}
                    <Box sx={{ display: 'flex', gap: 0.5, height: 6, mb: 2.5 }}>
                      {[1, 2, 3, 4].map((segment) => (
                        <Box
                          key={segment}
                          sx={{
                            flex: 1,
                            borderRadius: 1,
                            bgcolor: segment <= passwordStrength.score ? passwordStrength.color : '#e2e8f0',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      ))}
                    </Box>

                    {/* Requisitos */}
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {passwordRequirements.minLength ? (
                            <CheckCircle sx={{ fontSize: 16, color: '#06c270' }} />
                          ) : (
                            <Circle sx={{ fontSize: 16, color: '#cbd5e1' }} />
                          )}
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{ color: passwordRequirements.minLength ? '#06c270' : '#94a3b8' }}
                          >
                            Mín. 8 caracteres
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {passwordRequirements.hasUpperCase ? (
                            <CheckCircle sx={{ fontSize: 16, color: '#06c270' }} />
                          ) : (
                            <Circle sx={{ fontSize: 16, color: '#cbd5e1' }} />
                          )}
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{ color: passwordRequirements.hasUpperCase ? '#06c270' : '#94a3b8' }}
                          >
                            Letra maiúscula
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {passwordRequirements.hasNumber ? (
                            <CheckCircle sx={{ fontSize: 16, color: '#06c270' }} />
                          ) : (
                            <Circle sx={{ fontSize: 16, color: '#cbd5e1' }} />
                          )}
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{ color: passwordRequirements.hasNumber ? '#06c270' : '#94a3b8' }}
                          >
                            Um número
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {passwordRequirements.hasLowerCase ? (
                            <CheckCircle sx={{ fontSize: 16, color: '#06c270' }} />
                          ) : (
                            <Circle sx={{ fontSize: 16, color: '#cbd5e1' }} />
                          )}
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{ color: passwordRequirements.hasLowerCase ? '#06c270' : '#94a3b8' }}
                          >
                            Letra minúscula
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grow>
              )}
            </Box>

            {/* Confirmação da Nova Senha */}
            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirmação da Nova Senha"
              placeholder="Repita a nova senha"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword !== '' && newPassword !== confirmPassword}
              helperText={
                confirmPassword !== '' && newPassword !== confirmPassword
                  ? 'As senhas não coincidem'
                  : ''
              }
              sx={textFieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: '#6B7588' }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Botão Submit */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              startIcon={<VpnKey />}
              sx={{
                mt: 2,
                py: 1.5,
                bgcolor: colorPrimary,
                color: '#fff',
                fontWeight: 700,
                fontSize: '1rem',
                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(255, 102, 0, 0.25)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  bgcolor: '#ff6a1a',
                  boxShadow: '0 12px 32px rgba(255, 102, 0, 0.35)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              Atualizar Senha
            </Button>
          </Box>
      </Paper>
    </Fade>
  );
}